import { useCallback, type FC } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { logError } from "../../api/logError";
import { prefetch } from "../../api/prefetch";
import type { InputProps } from "../DynamicInput";
import { useDynamicFormState } from "../DynamicFormProvider";
import type { Option } from "../types";
import { isLabelAndValueArray } from "../types";
import type { InlineInputStateStyles } from "../inlineForm/inputs/InlineInputState";
import { OptionCategoryFormDialog } from "../../optionCategoryList/OptionCategoryFormDialog";
import { optionCategoryTextKeys } from "../../optionCategoryList/optionCategoryTypeConfig";
import { organisationOptionCategoryScope } from "../../optionCategoryList/optionCategoryScopes";
import type { OptionCategoryScope } from "../../optionCategoryList/optionCategoryScopes";
import { sharedOptionCategoryScope } from "../../enterpriseCustomer/sharedOptionCategoryScope";
import type { OptionCategorySelectOption, OptionCategoryType } from "../../api/optionCategories";
import { isEnterpriseCustomerScope } from "../../enterpriseCustomer/enterpriseCustomer";
import { usePermission } from "../../shared/permissions";
import { useCurrentCustomer, useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import type { CreateNewDialogProps } from "./MultiAutocomplete";
import { MultiAutocomplete } from "./MultiAutocomplete";

export type DynamicMultiSelectProps = InputProps<string[]> & {
  optionCategoryType: OptionCategoryType;
  inlineInputStateStyles?: InlineInputStateStyles;
};

const mapOptionCategoryByTypeToOptions = (optionCategory: OptionCategorySelectOption[]): Option[] => {
  return optionCategory.map(({ value, label, metadata }) => ({ value, label, description: metadata?.description }));
};

export const DynamicMultiSelect: FC<DynamicMultiSelectProps> = ({
  optionCategoryType,
  inlineInputStateStyles,
  ...props
}) => {
  const { savedAnswers } = useDynamicFormState();
  const currentCustomer = useCurrentCustomer();
  const currentOrganisation = useCurrentOrganisation();
  const { hasPermission } = usePermission();
  const scope = isEnterpriseCustomerScope({ currentCustomer, currentOrganisation })
    ? sharedOptionCategoryScope
    : organisationOptionCategoryScope;
  const selectOptionsQuery = useQuery(scope.selectOptionsQueryOptions(optionCategoryType));

  const answer = savedAnswers.find(answer => answer.questionId === props.questionCard.id);

  if (answer && !isLabelAndValueArray(answer.value))
    logError("DynamicMultiSelect: Answer value is not an array of label and value pairs.");

  const options: Option[] = selectOptionsQuery.data
    ? mapOptionCategoryByTypeToOptions(selectOptionsQuery.data.options)
    : answer && isLabelAndValueArray(answer.value)
      ? answer.value
      : [];

  const CreateNewOptionDialog = useCallback(
    (dialogProps: CreateNewDialogProps) => (
      <CreateOptionCategoryDialog {...dialogProps} scope={scope} categoryType={optionCategoryType} />
    ),
    [scope, optionCategoryType]
  );

  return (
    <MultiAutocomplete
      {...props}
      inlineInputStateStyles={inlineInputStateStyles}
      options={options}
      isLoading={selectOptionsQuery.isPending}
      placeholderKey="DynamicForm.Inline.InputPlaceholder"
      onMouseOver={() => prefetch(scope.selectOptionsQueryOptions(optionCategoryType))}
      chipVariant="choice"
      {...(hasPermission(scope.permission(optionCategoryType, "create"))
        ? { CreateNewDialog: CreateNewOptionDialog }
        : { noOptionsTextKey: "DynamicForm.Option.Empty" })}
    />
  );
};

const CreateOptionCategoryDialog = ({
  scope,
  categoryType,
  value,
  onClose,
  onCreated,
}: CreateNewDialogProps & { scope: OptionCategoryScope; categoryType: OptionCategoryType }) => {
  const createMutation = useMutation(scope.createMutationOptions());

  return (
    <OptionCategoryFormDialog
      mode="create"
      textKeys={optionCategoryTextKeys(categoryType, scope.name)}
      hasDescription={scope.hasDescription(categoryType)}
      defaultValues={{ label: value, description: "" }}
      onSubmit={async values => {
        const created = await createMutation.mutateAsync({ categoryType, values });
        onCreated({ value: created.value, label: values.label });
      }}
      onClose={onClose}
    />
  );
};
